// ShiftEditPopover.jsx — small popover for editing a single shift cell.
import React, { useEffect, useRef, useState } from "react";
import { X, CheckCircle } from "lucide-react";

const NEON = "#00FF99";
const AMBER = "#EF9F27";
const RED   = "#E24B4A";

const SHIFT_TYPES = [
  { value: "morning",   label: "Morning",   color: "#378ADD" },
  { value: "afternoon", label: "Afternoon", color: AMBER },
  { value: "night",     label: "Night",     color: "#7F77DD" },
  { value: "off",       label: "Off",       color: "#444" },
  { value: "oncall",    label: "On-call",   color: "#555" },
  { value: "custom",    label: "Custom",    color: NEON },
];

const TYPE_TO_TIMES = {
  morning:   { start: "07:00", end: "15:00" },
  afternoon: { start: "15:00", end: "23:00" },
  night:     { start: "23:00", end: "07:00" },
  off:       { start: "09:00", end: "17:30" },
  oncall:    { start: "00:00", end: "00:00" },
};

const hoursOf = (start, end) => {
  try {
    const [sh, sm] = start.split(":").map(Number);
    const [eh, em] = end.split(":").map(Number);
    let mins = (eh * 60 + em) - (sh * 60 + sm);
    if (mins <= 0) mins += 24 * 60;
    return (mins / 60).toFixed(1);
  } catch { return "0"; }
};

export default function ShiftEditPopover({
  shift, templates = [], anchor, onClose, onSave, onLeaveAction, userName,
}) {
  const ref = useRef(null);
  const [type, setType] = useState(shift?.shift_type || "off");
  const [start, setStart] = useState(shift?.start_time || "09:00");
  const [end, setEnd]     = useState(shift?.end_time   || "17:30");
  const [tplId, setTplId] = useState(shift?.template_id || "");
  const [notes, setNotes] = useState(shift?.notes || "");

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose?.(); };
    document.addEventListener("mousedown", handler);
    document.addEventListener("keydown", (e) => e.key === "Escape" && onClose?.());
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  useEffect(() => {
    if (type !== "custom" && TYPE_TO_TIMES[type]) {
      setStart(TYPE_TO_TIMES[type].start);
      setEnd(TYPE_TO_TIMES[type].end);
    }
  }, [type]);

  const save = () => {
    onSave?.({
      user_id:    shift.user_id,
      shift_date: shift.shift_date,
      shift_type: type,
      start_time: start,
      end_time:   end,
      is_off_day: type === "off",
      template_id: tplId || null,
      notes:      notes || null,
    });
  };

  // Position near anchor (right of cell, fallback below)
  const rect = anchor?.getBoundingClientRect?.();
  const top  = rect ? Math.min(rect.bottom + 6 + window.scrollY, window.scrollY + window.innerHeight - 380) : 100;
  const left = rect ? Math.min(rect.left + window.scrollX, window.scrollX + window.innerWidth - 360) : 100;

  return (
    <div
      ref={ref}
      style={{
        position: "absolute", top, left,
        zIndex: 200, width: 340,
        background: "#0d0d0d",
        border: `0.5px solid ${NEON}55`,
        borderRadius: 12,
        padding: 14,
        boxShadow: "0 8px 30px rgba(0,0,0,0.7)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", marginBottom: 10 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, color: "#fff", fontWeight: 600 }}>{userName}</div>
          <div style={{ fontSize: 11, color: "#666" }}>{shift?.shift_date}</div>
        </div>
        <button onClick={onClose} style={{ background: "transparent", border: "none", color: "#888", cursor: "pointer" }}>
          <X size={14} />
        </button>
      </div>

      {shift?.leave_status && (
        <div style={{
          padding: "8px 10px", borderRadius: 8, marginBottom: 10,
          background: shift.leave_status === "approved" ? "rgba(226,75,74,0.1)" : "rgba(239,159,39,0.08)",
          border: `0.5px solid ${shift.leave_status === "approved" ? RED : AMBER}55`,
          color: shift.leave_status === "approved" ? RED : AMBER,
          fontSize: 11,
        }}>
          {shift.leave_status === "approved" ? "On approved leave" : "Leave pending approval"}
          {shift.leave_id && shift.leave_status === "pending" && onLeaveAction && (
            <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
              <button onClick={() => onLeaveAction(shift.leave_id, "approve")}
                style={smallBtn(NEON)}>Approve</button>
              <button onClick={() => onLeaveAction(shift.leave_id, "reject")}
                style={smallBtn(RED)}>Reject</button>
            </div>
          )}
        </div>
      )}

      <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 10 }}>
        {SHIFT_TYPES.map((t) => (
          <button
            key={t.value}
            onClick={() => setType(t.value)}
            style={{
              padding: "4px 10px", borderRadius: 999, fontSize: 11, cursor: "pointer",
              border: type === t.value ? `0.5px solid ${NEON}88` : "0.5px solid #2a2a2a",
              background: type === t.value ? "rgba(0,255,153,0.08)" : "transparent",
              color: type === t.value ? NEON : "#bbb",
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {(type === "custom" || type !== "off") && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 8 }}>
          <input type="time" value={start} onChange={(e) => setStart(e.target.value)} style={inp} />
          <input type="time" value={end}   onChange={(e) => setEnd(e.target.value)}   style={inp} />
        </div>
      )}

      {templates.length > 0 && (
        <select value={tplId} onChange={(e) => {
          const id = e.target.value; setTplId(id);
          const t = templates.find((x) => x.id === id);
          if (t) {
            setType(t.shift_type || "custom");
            setStart(t.start_time);
            setEnd(t.end_time);
          }
        }} style={{ ...inp, marginBottom: 8 }}>
          <option value="">— Pick template —</option>
          {templates.map((t) => (
            <option key={t.id} value={t.id}>{t.name} ({t.start_time}-{t.end_time})</option>
          ))}
        </select>
      )}

      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        rows={2}
        placeholder="Notes (optional)"
        style={{ ...inp, fontFamily: "inherit", resize: "vertical", marginBottom: 8 }}
      />

      <div style={{ display: "flex", alignItems: "center", marginTop: 6 }}>
        <span style={{ fontSize: 11, color: "#666", flex: 1 }}>
          {type === "off" ? "Day off" : `${hoursOf(start, end)}h shift`}
        </span>
        <button onClick={onClose} style={{ ...smallBtn("#444"), marginRight: 6 }}>Cancel</button>
        <button onClick={save} style={smallBtn(NEON)}>
          <CheckCircle size={11} style={{ verticalAlign: "middle", marginRight: 4 }} />
          Save
        </button>
      </div>
    </div>
  );
}

const inp = {
  background: "#0a0a0a",
  border: "0.5px solid #2a2a2a",
  borderRadius: 8,
  padding: "6px 9px",
  fontSize: 12,
  color: "#fff",
  outline: "none",
  width: "100%",
};

const smallBtn = (color) => ({
  padding: "5px 12px", borderRadius: 8, fontSize: 11,
  background: color === NEON ? `rgba(0,255,153,0.12)` :
              color === RED  ? `rgba(226,75,74,0.12)`  : "rgba(255,255,255,0.04)",
  border: `0.5px solid ${color}55`, color, cursor: "pointer",
});
